import React, { useState } from 'react';
import { useEffect, useRef } from 'react'
import HtmlHead from '../../components/Head';
import Layout from '../../components/Layout';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import SubHeader from '../../components/SubHeader';
import styles from '../../styles/Podcast.module.css';
import Container from 'react-bootstrap/Container';
import Spinner from 'react-bootstrap/Spinner';
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Toast from 'react-bootstrap/Toast'
import ReactPlayer from 'react-player/file'; // we only play audio, do not include everything to reduce bundle size
import PodcastPlayList from "./PodcastPlaylist.js";
import PodcastPlayerBar from "./PodcastPlayerBar.js";
import PodcastItemRow from "./PodcastItemRow.js";
import * as LIB from '../../lib/common';
import _ from 'lodash';

const Podcast = ({ defaultPodcastData }) => {
    const downloadBaseUrl = "https://podcast.ziondaily.com/download.php?file=";
    const playbackBaseUrl = "https://podcast.ziondaily.com/media/";
    const INIT_NUM_MAX_ROWS = 200;

    const [podcastData, setPodcastData] = useState(defaultPodcastData);
    const [currentChannel, setCurrentChannel] = useState(0);
    const [currentMaxDisplayRow, setCurrentMaxDisplayRow] = useState(INIT_NUM_MAX_ROWS);
    const [currentAudio, setCurrentAudio] = useState({ channel: 0, id: null, file: '', title: '　' });
    const [shouldPlay, setShouldPlay] = useState(false);
    const [playerState, setPlayerState] = useState("stopped");
    const [totalDuration, setTotalDuration] = useState(0);
    const [currentPosition, setCurrentPosition] = useState(0);
    const [overflowMenuRow, setOverflowMenuRow] = useState(null);
    const [showToast, setShowToast] = useState(false);
    const [toastText, setToastText] = useState('');
    const [toastTimer, setToastTimer] = useState(null);
    const [shouldScrollToCurrentAudio, setShouldScrollToCurrentAudio] = useState(false);
    const [histories, setHistories] = useState({});
    const [lastSyncTime, setLastSyncTime] = useState(0);
    const [showSpinner, setShowSpinner] = useState(false);
    const player = useRef(null);
    const listRef = useRef(null);
    const [isClient, setIsClient] = useState(false);

    let audioListRowRefs = [];
    let currentHistories = {};

    // ================================================================================
    // perform side effect those only run on the first render
    // ================================================================================
    useEffect(() => {
        setIsClient(true)
    }, []);

    useEffect(() => {
        currentHistories = loadLocalHistories();
        setHistories(currentHistories);

        // resume last browsing channel
        let channel = localStorage.getItem("podcastHistory_currentChannel");
        if (channel != null) {
            setCurrentChannel(channel);
        }

        // resume last playback item
        let audio = JSON.parse(localStorage.getItem("podcastHistory_currentAudio"));
        if (audio != null) {
            setCurrentAudio(audio);

            // resume playback position
            if (currentHistories[audio.id] != null) {
                setCurrentPosition(currentHistories[audio.id].pos);
            }
        }

        fetchPodcastData(); // re-hydration on browser side

        window.addEventListener('scroll', handleOnScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleOnScroll);
    }, []);

    useEffect(() => {
        // scroll to the audio row
        if (shouldScrollToCurrentAudio) {
            let ref = audioListRowRefs[currentAudio.id];
            if (ref) {
                window.scrollTo({ top: ref.offsetTop, behavior: "smooth" });
            }

            setShouldScrollToCurrentAudio(false);

            setShowSpinner(false);
        }

    }, [currentChannel, currentMaxDisplayRow]);

    // save current position to local storage
    useEffect(() => {
        if (currentAudio.id) {
            modifyHistory(currentAudio.id, currentPosition, 0); // only add counter onEnd()

            // sync to server periodically
            if (getCurrentTime() - lastSyncTime > 30) {
                sendHistoryDataToServer([currentAudio.id]);
                setLastSyncTime(getCurrentTime()); // mark it no matter sync success or not so retry will not be too frequent when network fails
            }
        }
    }, [currentPosition]);

    useEffect(() => {
        localStorage.setItem("podcastHistory_currentChannel", currentChannel);
    }, [currentChannel]);

    useEffect(() => {
        localStorage.setItem("podcastHistory_currentAudio", JSON.stringify(currentAudio));
    }, [currentAudio]);

    // side effect for podcastData
    useEffect(() => {
        // play the audio specified on the location, if any  
        let currentLocation = window.location.href;
        let hash = currentLocation.substring(currentLocation.indexOf('#') + 1);

        if (hash) {
            hash = decodeURIComponent(hash);

            for (var c = 0; c < podcastData.length; c++) {
                for (var i = 0; i < podcastData[c].list.length; i++) {
                    let title = podcastData[c].list[i].title.replace(/[ （）]/g, '');

                    if (title == hash) {
                        // remove the hash from location
                        window.history.replaceState(null, currentAudio.title, LIB.getBasepath(`/podcast`));

                        //console.log('find ' + c + ', ' + i);
                        playItem(c, i);

                        setShowSpinner(true);
                        setCurrentMaxDisplayRow(9999);
                        setShouldScrollToCurrentAudio(true);
                        setCurrentChannel(c);

                        break;
                    }
                }
            }
        }
    }, [podcastData]);
    

    // ================================================================================
    // core functions
    // ================================================================================
    const fetchPodcastData = async () => {
        let cookies = parseCookie();
        let url = 'https://podcast.ziondaily.com/channel_list.php?list_items=all&mid=' + cookies.zd_mid;

        //console.log('fetch: ' + url);

        fetch(url, {
        }).then(response => response.json()
        ).then(data => {
            syncHistoryFromServerData(data);
            setPodcastData(data);
            syncPendingLocationHistoryToServer(data);
        });
    }

    function syncHistoryFromServerData(data) {
        // sync history from server to local storage
        for (var c = 0; c < data.length; c++) {
            for (var i = 0; i < data[c].list.length; i++) {
                let audioId = data[c].list[i].id;

                // init history for each item
                if (currentHistories[audioId] == null) {
                    currentHistories[audioId] = { localCount: 0, serverCount: 0, pos: 0, t: 0, pending: false };
                }

                // server history is newer than local history, update local value
                if (data[c].list[i].history > 0 && data[c].list[i].history > currentHistories[audioId].t) {
                    currentHistories[audioId].serverCount = parseInt(data[c].list[i].count);
                    currentHistories[audioId].pos = parseInt(data[c].list[i].pos);
                    currentHistories[audioId].t = parseInt(data[c].list[i].history);
                    currentHistories[audioId].pending = false;
                }
            }
        }

        //console.log('updated history from server: ');
        //console.log(currentHistories);

        setHistories(currentHistories);
        saveLocalHistories(currentHistories);
    }

    function syncPendingLocationHistoryToServer(data) {
        let cookies = parseCookie();

        if (cookies.zd_mid) {
            let syncIds = [];

            // bundle all history need to sync into one object
            for (var c = 0; c < data.length; c++) {
                for (var i = 0; i < data[c].list.length; i++) {
                    let audioId = data[c].list[i].id;

                    if (currentHistories[audioId] != null && currentHistories[audioId].pending) {
                        syncIds.push(audioId);
                    }
                }
            }

            if (syncIds.length) {
                sendHistoryDataToServer(syncIds);
            }
        }
    }

    async function sendHistoryDataToServer(syncIds) {
        let cookies = parseCookie();

        //console.log('cookies.zd_mid: ' + cookies.zd_mid);

        if (cookies.zd_mid) {
            let formBody = [];
            for (var i = 0; i < syncIds.length; i++) {
                let audioId = syncIds[i];

                let encodedKey = encodeURIComponent(audioId);
                let encodedValue = encodeURIComponent(Math.floor(currentHistories[audioId].pos));
                formBody.push(encodedKey + "=" + encodedValue);

                encodedKey = encodeURIComponent('count_' + audioId);
                encodedValue = encodeURIComponent(currentHistories[audioId].localCount);
                formBody.push(encodedKey + "=" + encodedValue);
            }
            formBody = formBody.join("&");

            let url = 'https://podcast.ziondaily.com/add_user_playback_history.php?mid=' + cookies.zd_mid;

            fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: formBody
            }).then(response => response.json()
            ).then(res => {
                //console.log('sync history result: ');
                //console.log(res);

                for (var i = 0; i < res.length; i++) { // server will return the list of item id successfully saved history
                    let audioId = res[i];
                    currentHistories[audioId].serverCount += currentHistories[audioId].localCount;
                    currentHistories[audioId].localCount = 0;
                    currentHistories[audioId].pending = false;
                }

                setHistories(currentHistories);
                saveLocalHistories(currentHistories);
            });
        }
    }

    const playItem = (channel, index) => {
        if (channel == currentChannel && index == getCurrentAudioIndex()) {
            if (playerState == 'playing') {
                return;
            }
        }

        //console.log('play item: ' + index + ': ' + podcastData[channel].list[index].file);

        let audio = podcastData[channel].list[index];
        audio.fullTitle = "《" + podcastData[channel].title + '》 ' + audio.title;
        audio.channel = channel;

        setCurrentAudio(audio);
        setTotalDuration(Math.floor(audio.duration)); // this data is from API, we will still use the player's duration when it's ready
        setPlayerState("buffering");

        // resume last playback position, if ever played
        if (histories[audio.id] != null && histories[audio.id].pos < audio.duration) {
            setCurrentPosition(histories[audio.id].pos);
        }
        else {
            setCurrentPosition(0);
        }

        setShouldPlay(true); // the audio player core will fire event when playing really starts
        // we will only change the player bar UI according to the event
    }

    // ================================================================================
    // player bar UI callbacks
    // ===============================================================================
    const handleOnClickTitle = () => {
        if (currentAudio.channel == currentChannel) {
            let ref = audioListRowRefs[currentAudio.id];
            if (ref) {
                window.scrollTo({ top: ref.offsetTop, behavior: "smooth" });
            }
            else {
                setShowSpinner(true);
                setShouldScrollToCurrentAudio(true);
                setCurrentMaxDisplayRow(9999);
            }
        }
        else {
            setShowSpinner(true);
            setCurrentMaxDisplayRow(9999);
            setShouldScrollToCurrentAudio(true);
            setCurrentChannel(currentAudio.channel);
        }
    }

    const handleOnClickPlayPause = () => {
        if (playerState == 'buffering')
            return;

        if (currentAudio.id == null) // never clicked any item yet, play the first item now
            playItem(0, 0);
        else // resume or pause the current selected item
            setShouldPlay(playerState == 'playing' ? false : true);
    }

    function getCurrentAudioIndex() {
        for (var index = 0; index < podcastData[currentChannel].list.length; index++) {
            if (podcastData[currentChannel].list[index].id == currentAudio.id) {
                return index;
            }
        }

        return -1;
    }

    const handleOnClickPrevTrack = () => {
        let currentAudioIndex = getCurrentAudioIndex();
        if (currentAudioIndex > 0) {
            playItem(currentAudio.channel, currentAudioIndex - 1);
        }
    }

    const handleOnClickNextTrack = () => {
        let currentAudioIndex = getCurrentAudioIndex();
        if (currentAudioIndex < podcastData[currentChannel].list.length - 1) {
            playItem(currentAudio.channel, currentAudioIndex + 1);
        }
    }


    // ================================================================================
    // audio player core event callback
    // ================================================================================
    const handleDuration = (duration) => {
        setTotalDuration(Math.floor(duration));

        // resume last played position (do not seek in onReady, it should be a bug!)
        if (histories[currentAudio.id] != null && histories[currentAudio.id].pos < currentAudio.duration) {
            player.current.seekTo(histories[currentAudio.id].pos);
        }
    }

    const handleProgress = (progress) => {
        if (playerState == 'playing') {
            setCurrentPosition(progress.playedSeconds);
        }
    }

    const handleOnPlay = () => {
        setPlayerState('playing');
    }

    const handleOnPause = () => {
        setPlayerState('paused');
    }

    //callback when mp3 just finish playing.
    const handleOnEnd = () => {
        //TODO:
        setShouldPlay(false);
        setPlayerState('stopped');

        modifyHistory(currentAudio.id, 0, 1); // increase playback counter
        sendHistoryDataToServer([currentAudio.id]); // sync to server immediately

        //determine the next action.
        let repeatMode = localStorage.getItem("podcastRepeatMode");
        let autoPlayMode = localStorage.getItem("podcastAutoPlay");        
        if (repeatMode == '1'){
            //repeat one
            playItem(currentChannel, getCurrentAudioIndex());
        }else if(autoPlayMode == 'on'){
            //auto play
            handleOnClickNextTrack();   
        }else{
            setCurrentPosition(0);
        }
    }

    const handleOnError = () => {
        setShouldPlay(false);
        setPlayerState('stopped');
    }

    const handleOnSeekBarChange = (e) => {
        let percent = parseFloat(e.target.value);
        let pos = totalDuration * percent;

        setCurrentPosition(pos);
        player.current.seekTo(pos);
    }


    // ================================================================================ 
    // helper functions
    // ================================================================================ 
    function parseCookie() {
        let cookies = {};
        let arr = document.cookie.split("; ");
        for (let i = 0; i < arr.length; i++) {
            let tuple = arr[i].split('=');
            cookies[tuple[0]] = decodeURIComponent(tuple[1]);
        }

        return cookies;
    }

    function saveLocalHistories(newHistories) {
        //console.log('save: ');
        //console.log(newHistories);
        localStorage.setItem("podcastHistories", JSON.stringify(newHistories));
    }

    function modifyHistory(id, pos, counter) {
        currentHistories = { ...histories };

        if (counter > 0) {
            currentHistories[id].localCount++;
        }
        else if (counter < 0) {
            currentHistories[id].localCount = -1 * currentHistories[id].serverCount;
        }

        currentHistories[id].pos = Math.floor(pos);
        currentHistories[id].t = getCurrentTime();
        currentHistories[id].pending = true;

        //console.log(currentHistories[id]);

        setHistories(currentHistories);
        saveLocalHistories(currentHistories);
    }

    function loadLocalHistories() {
        let json = localStorage.getItem("podcastHistories");
        let loadedHistories = json == null ? {} : JSON.parse(json);

        // init history records for all default podcast items
        let data = defaultPodcastData;

        for (var c = 0; c < data.length; c++) {
            for (var i = 0; i < data[c].list.length; i++) {
                let audioId = data[c].list[i].id;

                if (loadedHistories[audioId] == null) {
                    loadedHistories[audioId] = { localCount: 0, serverCount: 0, pos: 0, t: 0, pending: false };
                }
            }
        }

        //console.log('load: ');
        //console.log(loadedHistories);

        return loadedHistories;
    }

    function getCurrentTime() {
        return new Date().getTime() / 1000;
    }

    const toggleToast = (text) => {
        setToastText(text);
        setShowToast(true);

        if (toastTimer != null) // reset any pending timeout otherwise the toast may disappear too fast
            clearTimeout(toastTimer);

        let timer = setTimeout(() => {
            setShowToast(false);
        }, 2000);
        setToastTimer(timer);
    }

    function onOverflowButtonClick(e) {
        // alert();
        // return;

        console.log(e.currentTarget);

        e.stopPropagation();
        setOverflowMenuRow(e.currentTarget);
    }

    function onOverflowMenuClose(e) {
        setOverflowMenuRow(null);
    }

    function onOverflowMenuOption(option) {
        let index = overflowMenuRow.getAttribute('index');
        let item = podcastData[currentChannel].list[index];

        //console.log(option + ', ' + index);

        if (option == 'download') {
            // since the URL end with .mp3 so the CloudFlare cache rule for all MP3s also applies to it
            let url = downloadBaseUrl + item.file;
            window.open(url);
        }
        else if (option == 'setHistory') {
            let audioId = item.id;

            if (audioId == currentAudio.id) {
                setShouldPlay(false);
                setCurrentPosition(0);
            }

            modifyHistory(audioId, 0, 1);

            sendHistoryDataToServer([audioId]);
        }
        else if (option == 'unsetHistory') {
            let audioId = item.id;

            if (audioId == currentAudio.id) {
                setShouldPlay(false);
                setCurrentPosition(0);
            }

            modifyHistory(audioId, 0, -1);

            sendHistoryDataToServer([audioId]);
        }
        else if (option == 'share') {
            let url = process.env.domain + LIB.getBasepath(`/podcast`) + '#' + item.title.replace(/[ （）]/g, '');
            navigator.clipboard.writeText(url);
            toggleToast('已複製連結');
        }

        setOverflowMenuRow(null);
    }

    function handleOnClickRewind() {
        player.current.seekTo(currentPosition - 15);
    }
    
    function handleOnClickForward() {
        player.current.seekTo(currentPosition + 15);
    }

    const handleOnScroll = (event) => {
        let y = window.pageYOffset + window.innerHeight;

        if (y > listRef.current.clientHeight - 1000) {
            setCurrentMaxDisplayRow(9999);
        }
    };



    // ================================================================================ 
    // render the top filter tab buttons 
    // ================================================================================ 
    const renderTab = () => {
        return (
            <Container fluid className={styles.musTabHolder}>
                <Row style={{ padding: 0, margin: 0 }}>
                    {
                        podcastData.map(function (item, index) {
                            return (
                                <Col md={4} xs={6} className={styles.musicCol} key={index} onClick={() => setCurrentChannel(index)}>
                                    <div className={currentChannel == index ? styles.musTabActive : styles.musTab}>{item.title}</div>
                                </Col>
                            )
                        })
                    }
                </Row>
            </Container>
        );
    }


    // ================================================================================ 
    // overflow menu
    // ================================================================================ 
    let overflowMenuOptions = '';

    if (overflowMenuRow) {
        let index = overflowMenuRow.getAttribute('index');
        let duration = Math.floor(podcastData[currentChannel].list[index].duration);
        let audioId = podcastData[currentChannel].list[index].id;
        let played = histories[audioId] == null ? 0 : histories[audioId].pos;
        let playCount = 0;
        if (histories[audioId] && histories[audioId].serverCount) playCount += histories[audioId].serverCount;
        if (histories[audioId] && histories[audioId].localCount) playCount += histories[audioId].localCount;
        let options;

        if (played == 0 && playCount > 0) {
            options = [
                { key: 'download', text: '下載 MP3' },
                { key: 'unsetHistory', text: '標記為未播放' },
                { key: 'share', text: '複製分享連結' }
            ];
        }
        else {
            options = [
                { key: 'download', text: '下載 MP3' },
                { key: 'setHistory', text: '標記為播放完畢' },
                { key: 'share', text: '複製分享連結' }
            ];
        }

        overflowMenuOptions = options.map(function (option) {
            return (
                <MenuItem key={option.key} onClick={() => onOverflowMenuOption(option.key)}>
                    {option.text}
                </MenuItem>
            )
        });
    }


    // ================================================================================ 
    // main render function
    // ================================================================================ 
    return (
        <Layout>
            <title>{{title: currentAudio.id == null ? '昔日信息' : currentAudio.title}}</title>
            <Header />
            <SubHeader data='昔日信息' />

            <div className="viewport">

                <Toast show={showToast} className={styles.toast}>
                    <Toast.Body>{toastText}</Toast.Body>
                </Toast>

                <Spinner className={styles.loading} style={showSpinner ? { display: 'block' } : { display: 'none' }} animation="border" role="status"></Spinner>
                    
                {/* row UI of the playlist */}
                <Container fluid style={{ padding: 0 }}>
                    <Row className={styles.zeroMarPad}>
                        <Col className={`d-block d-lg-none ${styles.zeroMarPad}`}>
                            {renderTab()}
                        </Col>
                        <Col lg={8} md={12} className={`${styles.zeroMarPad}`}>
                            <div ref={listRef} className={styles.itemRowHolder} maxrow={currentMaxDisplayRow}>
                                {
                                    currentChannel > podcastData.length - 1 ? `<div></div>` :
                                        podcastData[currentChannel].list.map(function (item, index) {
                                            if (index <= currentMaxDisplayRow) {
                                                return (
                                                    <PodcastItemRow
                                                        innerRef={ref => (audioListRowRefs[item.id] = ref)}
                                                        key={index}
                                                        index={index}
                                                        item={item}
                                                        history={histories == null ? null : histories[item.id]}
                                                        ariaOwns={Boolean(overflowMenuRow) ? 'long-menu' : undefined}
                                                        image={podcastData[currentChannel].image}
                                                        selected={item.id == currentAudio.id ? true : false}
                                                        onClick={() => playItem(currentChannel, index)}
                                                        onOverflowButtonClick={(e) => onOverflowButtonClick(e)} 
                                                    />
                                                );
                                            }
                                        })
                                }
                            </div>
                        </Col>
                        <Col lg={4} className={`d-none d-lg-block ${styles.zeroMarPad}`}>
                            <div className={styles.sticky}>
                                {/* side menu, right side */}
                                <PodcastPlayList data={podcastData} onSelectChannel={(channel) => { window.scrollTo(0, 0); setCurrentMaxDisplayRow(INIT_NUM_MAX_ROWS); setCurrentChannel(channel); }} />
                            </div>
                        </Col>
                    </Row>
                </Container>
    
                {/* Feature button menu: download, mark as finish, copy link */}
                <Menu
                    id="long-menu"
                    anchorEl={overflowMenuRow}
                    open={Boolean(overflowMenuRow)}
                    onClose={onOverflowMenuClose}
                // PaperProps={{ style: { maxHeight: 55 * overflowMenuOptions.length, width: 200 } }}
                >
                    {overflowMenuOptions}
                </Menu>

            </div>

            {
                //progress bar
                isClient ? <ReactPlayer
                    ref={player}
                    url={playbackBaseUrl + currentAudio.file}
                    width="1px"
                    height="1px"
                    progressInterval={500}
                    controls={false}
                    playing={shouldPlay}
                    onDuration={handleDuration}
                    onProgress={handleProgress}
                    onPlay={handleOnPlay}
                    onPause={handleOnPause}
                    onEnded={handleOnEnd}
                    onError={handleOnError}
                    loop={false}
                /> : <></>
            }
            
            {/* bottom player bar area */}
            <PodcastPlayerBar
                title={currentAudio.fullTitle}
                totalDuration={totalDuration}
                currentPosition={currentPosition}
                playPauseButtonState={playerState}
                setPlayPauseButtonState={()=>setPlayerState()}
                onClickTitle={() => handleOnClickTitle()}
                onClickPlayPause={() => handleOnClickPlayPause()}
                onClickPrevTrack={() => handleOnClickPrevTrack()}
                onClickNextTrack={() => handleOnClickNextTrack()}
                onSeekBarChanged={(e) => handleOnSeekBarChange(e)}
                onClickRewind={() => handleOnClickRewind()}
                onClickForward={() => handleOnClickForward()}
                onToggleRepeat={(state) => toggleToast(state == '0' ? '停止自動播放' : (state == '1' ? '自動重複播放' : '自動播放下一篇'))}
            />

            <Footer />
        </Layout>
    );
}

export default Podcast;

export async function getStaticProps() {
    const res = await fetch('https://podcast.ziondaily.com/channel_list.php?list_items=all');
    const defaultPodcastData = await res.json();

    return {
        props: { defaultPodcastData }, revalidate: 86400
    }
}